import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type React from 'react';
import { useKnownClusters } from '@/hooks/useKnownClusters';
import {
  filterMentionClusters,
  mentionAtCaret,
  type KnownCluster,
} from '@/utils/clusterReconcile';

interface Options {
  value: string;
  setValue: (next: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  /** IME composition in progress (MessageInput tracks it) */
  composing: boolean;
}

export interface MentionSuggestionsState {
  open: boolean;
  options: KnownCluster[];
  activeIndex: number;
  listId: string;
  select: (cluster: KnownCluster) => void;
  setActiveIndex: (index: number) => void;
  /** call with the textarea's caret after every change / caret move */
  syncCaret: (caret: number | null) => void;
  /** true when the key was the list's (the caller must not also send) */
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => boolean;
}

/**
 * STORY-300: @-mention suggestions for the chat input. Offered only when the
 * deployment has more than one live cluster (multi_cluster_available) and the
 * caret sits in an @token at a mention position. Picking one inserts
 * '@slug ' — the backend parses and strips it; the client sends the raw text.
 * Enter selects while the list is open and sends otherwise; nothing selects
 * during IME composition.
 */
export function useMentionSuggestions({
  value, setValue, textareaRef, composing,
}: Options): MentionSuggestionsState {
  const { clusters, multiClusterAvailable } = useKnownClusters();
  const [caret, setCaret] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const pendingCaret = useRef<number | null>(null);
  const listId = `mention-list-${useId().replace(/:/g, '')}`;

  const query = caret === null ? null : mentionAtCaret(value, caret);
  const options = query && multiClusterAvailable
    ? filterMentionClusters(clusters, query.partial)
    : [];
  const open = query !== null && options.length > 0 && dismissedAt !== query.start;
  const active = open ? Math.min(activeIndex, options.length - 1) : -1;

  const queryKey = query ? `${query.start}:${query.partial}` : '';
  useEffect(() => {
    setActiveIndex(0);
    if (!query) setDismissedAt(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  useLayoutEffect(() => {
    const pos = pendingCaret.current;
    const el = textareaRef.current;
    if (pos !== null && el) {
      el.setSelectionRange(pos, pos);
      pendingCaret.current = null;
    }
  }, [value, textareaRef]);

  const select = (cluster: KnownCluster) => {
    if (!query) return;
    const rest = value.slice(query.end).replace(/^ /, '');
    const inserted = `@${cluster.slug} `;
    const pos = query.start + inserted.length;
    pendingCaret.current = pos;
    setCaret(pos);
    setValue(value.slice(0, query.start) + inserted + rest);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (!open || composing || e.nativeEvent.isComposing || e.keyCode === 229) return false;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((active + 1) % options.length);
        return true;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((active - 1 + options.length) % options.length);
        return true;
      case 'Enter':
      case 'Tab':
        if (e.shiftKey) return false;
        e.preventDefault();
        select(options[active]);
        return true;
      case 'Escape':
        e.preventDefault();
        setDismissedAt(query!.start);
        return true;
      default:
        return false;
    }
  };

  return {
    open,
    options,
    activeIndex: active,
    listId,
    select,
    setActiveIndex,
    syncCaret: setCaret,
    onKeyDown,
  };
}
